import type {PropsWithChildren} from 'react'
import {clsx} from 'clsx'
import {useSlots} from '@primer/react/experimental'
import {Blankslate} from './components/Blankslate'
import {Row} from './components/Row'
import {SkeletonRow as DefaultSkeletonRow} from './components/SkeletonRow'
import {Header} from './components/Header'
import {BoxHeader} from './components/BoxHeader'
import styles from './PanelList.module.css'
import {StaticProvider, useStatic} from './contexts/StaticContext'
import {EmptyProvider, useEmpty} from './contexts/EmptyContext'
import {LoadingProvider, useLoading} from './contexts/LoadingContext'

PanelList.BoxHeaderActions = BoxHeaderActions
PanelList.BoxHeader = BoxHeader
PanelList.HeaderActions = HeaderActions
PanelList.Header = Header
PanelList.Blankslate = Blankslate
PanelList.Row = Row
PanelList.Rows = Rows
PanelList.SkeletonRow = SkeletonRow

export {PanelList}

const panelSlotConfig = {
  boxHeaderActions: PanelList.BoxHeaderActions,
  boxHeader: PanelList.BoxHeader,
  headerActions: PanelList.HeaderActions,
  header: PanelList.Header,
  blankslate: PanelList.Blankslate,
  rows: PanelList.Rows,
  skeletonRow: PanelList.SkeletonRow,
}

export type PanelListProps = {
  title?: string
  subtitle?: string
  boxTitle?: string
  isLoading?: boolean
  isEmpty?: boolean
  isPreview?: boolean
  condensed?: boolean
  className?: string
}

function PanelList({children, isPreview, condensed, isEmpty, isLoading, ...props}: PropsWithChildren<PanelListProps>) {
  return (
    <StaticProvider isCondensed={condensed} isPreview={isPreview}>
      <EmptyProvider isEmpty={isEmpty}>
        <LoadingProvider isLoading={isLoading}>
          <PanelListInner {...props}>{children}</PanelListInner>
        </LoadingProvider>
      </EmptyProvider>
    </StaticProvider>
  )
}

type InnerPanelListProps = Omit<PanelListProps, 'isLoading' | 'isEmpty' | 'isPreview' | 'condensed'>

function PanelListInner({
  children: slottedChildren,
  boxTitle,
  title,
  subtitle,
  className,
}: PropsWithChildren<InnerPanelListProps>) {
  const isLoading = useLoading()
  const isEmpty = useEmpty()
  const {isCondensed} = useStatic()

  const [{boxHeaderActions, boxHeader, headerActions, header, blankslate, rows, skeletonRow}, children] = useSlots(
    slottedChildren,
    panelSlotConfig,
  )
  let content = null
  if (isLoading && isEmpty) {
    content = (
      <ul data-testid="PanelList.SkeletonRows" aria-busy="true">
        {skeletonRow ?? <DefaultSkeletonRow />}
        {skeletonRow ?? <DefaultSkeletonRow />}
      </ul>
    )
  } else if (isEmpty) {
    // Render a default blankslate if none is given
    content = blankslate ?? <Blankslate heading="No data" />
  } else {
    content = rows
  }
  return (
    <div className={clsx(styles.PanelList, 'd-flex flex-column gap-3', isCondensed && styles.Condensed, className)}>
      {header ??
        (title && (
          <Header title={title} subtitle={subtitle}>
            <Header.Actions>{headerActions}</Header.Actions>
          </Header>
        ))}
      <div className="Box">
        {boxHeader ??
          (boxTitle ? (
            <BoxHeader title={boxTitle} hidePreviewLabel={!!(title || header)}>
              <BoxHeader.Actions>{boxHeaderActions}</BoxHeader.Actions>
            </BoxHeader>
          ) : null)}
        {content}
        {children}
      </div>
    </div>
  )
}

function BoxHeaderActions({children}: PropsWithChildren) {
  return children
}
BoxHeaderActions.displayName = 'PanelList.BoxHeaderActions'

function HeaderActions({children}: PropsWithChildren) {
  return children
}
HeaderActions.displayName = 'PanelList.HeaderActions'

function Rows({children}: PropsWithChildren) {
  return <ul>{children}</ul>
}
Rows.displayName = 'PanelList.Rows'

function SkeletonRow({children}: PropsWithChildren) {
  return children
}
SkeletonRow.displayName = 'PanelList.SkeletonRow'
